import { Bundle, Messages } from '@dojo/framework/i18n/i18n';
import { difference } from 'collections';
import { LZString } from '@holmescorp/lz-string';
import * as moment from 'moment';

import { publish } from 'pub-sub';

import { db } from '../database';
import { SorSS } from '../interfaces';

export const DynlsCacheRecordSchema: string = '++, &namespace, changed';

export class DynlsCacheRecord {
	constructor(public namespace: string, public data: string, public changed?: string) {
		//
	}

	public async save(): Promise<void> {
		this.changed = moment().toISOString();
		await db.dynls.put(this);
	}

	public age(): number {
		const { changed } = this;
		if (changed) {
			const now = moment();
			const old = moment(changed);
			return now.diff(old, 'hours');
		}
		return 0;
	}
}

const compress = (data: Record<string, any>) => {
	return LZString.compressToUTF16(JSON.stringify(data));
};

const decompress = (data: string) => {
	return JSON.parse(LZString.decompressFromUTF16(data) || '{"locales": {"en-us": {}}}');
};

const process_namespaces = (nsnames: SorSS, ignore: string[], addLpl: boolean): string[] => {
	const ignoreNs = new Set(ignore);
	const left = new Set(
		nsnames.length > 0
			? typeof nsnames[0] === 'string'
				? (nsnames as string[])
				: (nsnames as string[][]).map((v) => v[0])
			: []
	);
	addLpl && left.add('lpl1');
	return Array.from(difference(left, ignoreNs));
};

export async function namespaces({
	svc_call,
	ignore = [],
	addLpl = false
}: {
	svc_call: () => Promise<SorSS>;
	ignore?: string[];
	addLpl?: boolean;
}): Promise<string[]> {
	let nsrecord = await db.dynls.where({ namespace: 'namespaces' }).first();
	if (nsrecord === undefined) {
		const nsnames = await svc_call();
		const data = process_namespaces(nsnames, ignore, addLpl);
		nsrecord = new DynlsCacheRecord('namespaces', JSON.stringify(data));
		await nsrecord.save();
	} else {
		const { sitews } = window.hcstatic;
		if (sitews.is_open() && nsrecord.age() > 2) {
			const refresh = async () => {
				const nsnames = await svc_call();
				const data = process_namespaces(nsnames, ignore, addLpl);
				db.dynls.where({ namespace: 'namespaces' }).modify({
					changed: moment().toISOString(),
					data: JSON.stringify(data)
				});
			};
			refresh();
		}
	}
	return JSON.parse(nsrecord.data);
}

/**
 * Background process to refresh dynls. Refresh process is single-entry per namespace,
 * so it's safe to call it multiple times.
 */
const refresh_active = new Set<string>();
const last_refresh = { value: '', running: false };
const MINUTE = 60 * 1000;

async function announce_refresh() {
	if (last_refresh.running) {
		return;
	}
	last_refresh.running = true;

	const try_announce = () => {
		if (last_refresh.value !== '') {
			const old = moment(last_refresh.value);
			const now = moment();
			if (now.diff(old, 'minutes') > 7) {
				last_refresh.value = '';
				publish('refresh/dynlscache');
			}
		}
	};
	setTimeout(try_announce, 3 * MINUTE);
}

function refresh_bundle<T>({ ns, svc_call }: { ns: string; svc_call: () => Promise<T> }) {
	if (refresh_active.has(ns)) {
		return;
	}
	refresh_active.add(ns);

	const { sitews } = window.hcstatic;
	const refresh = async () => {
		const data = await svc_call();
		db.dynls.where({ namespace: ns }).modify({
			changed: moment().toISOString(),
			data: compress(data)
		});
	};

	const try_refresh = async () => {
		const nsrecord = await db.dynls.where({ namespace: ns }).first();
		if (sitews.is_open() && nsrecord && nsrecord.age() > 2) {
			await refresh();
			last_refresh.value = moment().toISOString();
		}
		setTimeout(try_refresh, 10 * MINUTE);
	};

	try_refresh();
	announce_refresh();
}

export async function get_namespace_bundle<T>({
	ns,
	svc_call
}: {
	ns: string;
	svc_call: () => Promise<T>;
}): Promise<Bundle<Messages>> {
	const { sitews } = window.hcstatic;
	let nsrecord = await db.dynls.where({ namespace: ns }).first();
	if (nsrecord === undefined) {
		const data = await svc_call();
		nsrecord = new DynlsCacheRecord(ns, compress(data));
		await nsrecord.save();
	} else if (sitews.is_open() && nsrecord.age() > 2) {
		const data = await svc_call();
		await db.dynls.where({ namespace: ns }).modify({
			changed: moment().toISOString(),
			data: compress(data)
		});
		nsrecord = await db.dynls.where({ namespace: ns }).first();
	}
	refresh_bundle({ ns, svc_call });

	const data = decompress(nsrecord!.data);
	const bundle: Bundle<Messages> = {
		locales: {},
		messages: data.locales['en-us']
	};
	Object.keys(data.locales).forEach((locale) => {
		bundle.locales![locale] = () => {
			return { ...data.locales[locale] };
		};
	});
	return bundle;
}
